import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Scale, GraduationCap, Users, HandHeart, ArrowRight } from "lucide-react";
import DharmaWheel from "../components/DharmaWheel";
import BabasahebPortrait from "../components/BabasahebPortrait";
import ImpactStrip from "../components/ImpactStrip";
import CausesPreview from "../components/CausesPreview";
import PatronQuote from "../components/PatronQuote";

const PILLARS = [
  { icon: BookOpen, title: "Dhamma", subtitle: "The Path of Wisdom", color: "#001B3A",
    desc: "Rooted in the teachings of the Buddha: ethical living, mindfulness, and insight." },
  { icon: Scale, title: "Equality", subtitle: "Equal Rights for All", color: "#E07A5F",
    desc: "A dignified life for every human being, free from caste, class, and discrimination." },
  { icon: GraduationCap, title: "Education", subtitle: "Knowledge is Power", color: "#CFA052",
    desc: "Lifelong learning as the foundation of emancipation and social progress." },
  { icon: Users, title: "Community", subtitle: "Stronger Together", color: "#0A2540",
    desc: "A UK-wide sangha supporting members through friendship and collective effort." },
  { icon: HandHeart, title: "Compassion", subtitle: "Service to Humanity", color: "#A33F3F",
    desc: "Karuṇā in action: caring for the vulnerable, uplifting those who need support." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section data-testid="home-hero" className="relative overflow-hidden bg-navy text-pearl">
        <div
          className="absolute inset-0 opacity-[0.28]"
          style={{
            backgroundImage:
              "url('/images/1hv4vej6-34a5f882-4e27-4725-98d6-99cfc887ac1a.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/80 to-navy" />
        <div className="absolute -right-32 top-1/2 -translate-y-1/2 hidden md:block opacity-30">
          <DharmaWheel size={720} color="#CFA052" ringColor="#CFA052" />
        </div>

        <div className="relative section-container py-28 lg:py-40">
          <motion.p
            initial="hidden" animate="visible" variants={fadeUp} custom={0}
            className="eyebrow text-gold"
          >
            Established 2026 · United Kingdom
          </motion.p>
          <motion.h1
            initial="hidden" animate="visible" variants={fadeUp} custom={1}
            className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl text-pearl"
          >
            A just, peaceful and enlightened society, <span className="text-gold italic">together in Dhamma.</span>
          </motion.h1>
          <motion.p
            initial="hidden" animate="visible" variants={fadeUp} custom={2}
            className="mt-8 text-lg text-pearl/80 max-w-2xl leading-relaxed"
          >
            Ambedkarite Buddhist Global Network UK advances Buddhist education,
            meditation, equality and public community participation through
            in-person activities and our own <strong className="text-pearl">
            charitable digital platform</strong>. CIO application in preparation
            with the Charity Commission for England and Wales.
          </motion.p>
          <motion.div
            initial="hidden" animate="visible" variants={fadeUp} custom={3}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/membership"
              data-testid="hero-join-btn"
              className="px-7 py-3.5 bg-gold text-navy hover:bg-gold-light transition-colors rounded-sm font-medium tracking-wide inline-flex items-center gap-2"
            >
              Become a member <ArrowRight size={18} />
            </Link>
            <Link
              to="/about"
              data-testid="hero-learn-btn"
              className="px-7 py-3.5 border border-pearl/30 text-pearl hover:bg-pearl/10 transition-colors rounded-sm tracking-wide"
            >
              Learn our mission
            </Link>
          </motion.div>

          <div className="mt-20 grid grid-cols-3 gap-8 max-w-xl border-t border-pearl/20 pt-10">
            {[
              { n: "Public benefit", l: "Mission" },
              { n: "Physical + digital", l: "Delivery" },
              { n: "Open to all", l: "Participation" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-display text-2xl text-gold">{s.n}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-pearl/60 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MOTTO */}
      <section data-testid="home-motto" className="py-20 bg-pearl">
        <div className="section-container text-center">
          <p className="eyebrow">Babasaheb's Clarion Call</p>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl text-navy">
            <span className="text-saffron italic">Educate.</span> <span className="text-gold italic">Agitate.</span> <span className="italic">Organise.</span>
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-slate-600 leading-relaxed">
            These three words, spoken by Dr. B.R. Ambedkar a century ago, remain our north star.
            Educate the mind. Agitate against injustice. Organise the community.
          </p>
          <div className="mt-10 inline-flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs uppercase tracking-[0.3em] text-navy/80">
            <span className="text-saffron">Educate</span>
            <span className="text-saffron">·</span>
            <span className="text-saffron">Meditate</span>
            <span className="text-saffron">·</span>
            <span className="text-saffron">Empower</span>
            <span className="text-saffron">·</span>
            <span className="text-saffron">Elevate</span>
          </div>
          <p className="mt-3 text-xs italic text-slate-500">
            Rooted in Dhamma. Inspired by Babasaheb. Committed to humanity.
          </p>
        </div>
      </section>

      {/* FIVE PILLARS */}
      <section data-testid="home-pillars" className="py-24 bg-white">
        <div className="section-container">
          <div className="max-w-3xl">
            <p className="eyebrow">Our foundation</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy">Five pillars that shape everything we do.</h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
            {PILLARS.map((p, i) => {
              const span = i < 2 ? "lg:col-span-6" : "lg:col-span-4";
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  className={`${span} group relative bg-pearl border border-navy/10 p-8 rounded-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-500`}
                  data-testid={`pillar-${p.title.toLowerCase()}`}
                >
                  <div
                    className="inline-flex items-center justify-center w-14 h-14 rounded-sm mb-6"
                    style={{ backgroundColor: `${p.color}15` }}
                  >
                    <Icon size={26} strokeWidth={1.5} style={{ color: p.color }} />
                  </div>
                  <div className="eyebrow text-[10px]">{p.subtitle}</div>
                  <h3 className="mt-2 font-display text-3xl text-navy">{p.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed text-[15px]">{p.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* IMPACT STRIP — live numbers */}
      <ImpactStrip />

      {/* CAUSES — 4 themed funds */}
      <CausesPreview />

      {/* PATRON QUOTE */}
      <PatronQuote />

      {/* ABOUT TEASER */}
      <section data-testid="home-about-teaser" className="py-24 bg-pearl">
        <div className="section-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <p className="eyebrow">Our Inspiration</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy leading-tight">
              Babasaheb Dr. B.R. Ambedkar, architect of modern conscience.
            </h2>
            <p className="mt-6 text-slate-700 leading-relaxed">
              Father of the Indian Constitution and a lifelong champion of equality and justice,
              Dr. Ambedkar embraced the Buddha's Dhamma as a path that affirms human dignity. ABGN-UK carries
              his vision to the United Kingdom: a cloud-based, member-led society open to all.
            </p>
            <blockquote className="mt-8 border-l-4 border-gold pl-6 italic font-display text-2xl text-navy">
              "I measure the progress of a community by the degree of progress which women have achieved."
              <span className="block mt-2 not-italic text-sm text-slate-500">— Dr. B.R. Ambedkar</span>
            </blockquote>
            <Link
              to="/about"
              data-testid="home-about-link"
              className="mt-8 inline-flex items-center gap-2 text-navy border-b border-navy/30 hover:border-navy pb-1"
            >
              Read the full story <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <BabasahebPortrait />
          </div>
        </div>
      </section>

      {/* RECENT GATHERING */}
      <section data-testid="home-recent-gathering" className="py-24 bg-white">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <div className="relative overflow-hidden rounded-sm border border-navy/10 bg-pearl">
                <img
                  src="/images/1hv4vej6-34a5f882-4e27-4725-98d6-99cfc887ac1a.png"
                  alt="Buddha Purnima Celebration, Stockport Meeting House, 2nd May 2026"
                  className="w-full h-auto"
                />
              </div>
            </div>
            <div className="lg:col-span-5 order-1 lg:order-2">
              <p className="eyebrow">Recent gathering</p>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy leading-tight">
                Buddha Purnima 2026, at Stockport Meeting House.
              </h2>
              <p className="mt-6 text-slate-700 leading-relaxed">
                On the 2nd of May 2026, our community came together at the Stockport Meeting House for a
                beautiful day filled with peace, Dhamma and togetherness: meditation, Dhamma talk,
                community reflections, cultural programme and a shared lunch.
              </p>
              <p className="mt-4 text-slate-700 leading-relaxed italic">
                With deep gratitude to Bhante Premanand for guiding us in Dhamma and meditation, and to every
                friend, family and volunteer who made the day special.
              </p>
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs uppercase tracking-[0.18em] text-slate-600">
                <div className="border border-navy/10 px-3 py-2 rounded-sm">Meditation</div>
                <div className="border border-navy/10 px-3 py-2 rounded-sm">Dhamma Talk</div>
                <div className="border border-navy/10 px-3 py-2 rounded-sm">Reflections</div>
                <div className="border border-navy/10 px-3 py-2 rounded-sm">Cultural</div>
                <div className="border border-navy/10 px-3 py-2 rounded-sm">Lunch</div>
                <div className="border border-navy/10 px-3 py-2 rounded-sm">Togetherness</div>
              </div>
              <p className="mt-8 text-gold font-display text-xl italic">Jai Bhim · Namo Buddhāya</p>
            </div>
          </div>
        </div>
      </section>

      {/* CHARITABLE DIGITAL PLATFORM */}
      <section data-testid="home-platform" className="py-24 bg-navy text-pearl">
        <div className="section-container">
          <div className="max-w-3xl">
            <p className="eyebrow text-saffron">Our charitable digital platform</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-pearl leading-tight">
              A Sangha you can join, wherever you are.
            </h2>
            <p className="mt-6 text-pearl/80 leading-relaxed">
              Alongside our in-person gatherings, ABGN-UK has built its own digital
              platform &mdash; charitable infrastructure that lets every member, no
              matter where they live in the UK, take part in live meditation,
              Dhamma teachings, community meetings and structured learning.
            </p>
            <p className="mt-3 text-pearl/70 leading-relaxed text-sm">
              It exists to remove practical barriers to participation: distance
              across UK cities, mobility limitations, caring responsibilities,
              elderly participation, and people without a local Buddhist centre.
              It is not a product or a service &mdash; it is the way we deliver
              our public-benefit mission online.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="home-platform-activities">
            {[
              "Live meditation sessions",
              "Live Buddhist teachings",
              "Dhamma talks",
              "Community meetings",
              "Online public events",
              "Structured courses",
              "Youth learning programmes",
              "Volunteer collaboration",
              "Study circles",
              "Wellbeing discussions",
              "Hybrid participation",
              "Remote engagement",
            ].map((a) => (
              <div key={a} className="border border-pearl/15 bg-pearl/5 rounded-sm px-4 py-3 text-sm text-pearl/85">
                {a}
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/public-benefit"
              data-testid="home-platform-pb-link"
              className="px-5 py-2.5 bg-saffron text-navy rounded-sm text-sm font-medium hover:bg-saffron-light"
            >
              How this benefits the public
            </Link>
            <Link
              to="/platform"
              data-testid="home-platform-enter"
              className="px-5 py-2.5 border border-pearl/30 text-pearl rounded-sm text-sm hover:bg-pearl/10"
            >
              Enter the member platform &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* REFORMERS */}
      <section data-testid="home-reformers" className="py-24 bg-pearl">
        <div className="section-container">
          <div className="max-w-3xl">
            <p className="eyebrow">Our lineage of conscience</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy">Reformers who lit the path.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              ABGN-UK draws inspiration from a lineage of Indian visionaries: men and women who stood for
              justice, self-respect and the dignity of every human being.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { name: "Samrat Ashoka", role: "304 BCE — 232 BCE", img: "/images/p79lwkvj-f4c400bb-78f4-417c-93ad-866e29fe3e97.png", id: "ashoka" },
              { name: "Chhatrapati Shivaji Maharaj", role: "1630 — 1680", img: "/images/bvu9v8br-1567b038-0493-4561-b578-a0ab5b0d33e2.jpeg", id: "shivaji" },
              { name: "Mahatma Jyotirao Phule", role: "1827 — 1890", img: "/images/vqj1e8g6-804f88e5-cf8c-489e-a6b2-1bde052cec84.jpeg", id: "phule" },
              { name: "Periyar E.V. Ramasamy", role: "1879 — 1973", img: "/images/stg6ijqo-a859a2d4-fb40-4e44-8a5b-5f6278ccfba3.jpeg", id: "periyar" },
              { name: "Dr. B.R. Ambedkar", role: "1891 — 1956", id: "ambedkar", ambedkar: true },
              { name: "Anna Bhau Sathe", role: "1920 — 1969", img: "/images/mu0u5i90-3c3d1e73-15f0-4597-b6df-55efedd8e23b.jpeg", id: "annabhau" },
            ].map((r) => (
              <Link
                key={r.id}
                to={`/reformers#${r.id}`}
                data-testid={`home-reformer-${r.id}`}
                className="group block bg-white border border-navy/10 rounded-sm overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-500"
              >
                {r.ambedkar ? (
                  <BabasahebPortrait className="rounded-none border-0" />
                ) : (
                  <div className="aspect-[4/5] overflow-hidden bg-white">
                    <img
                      src={r.img}
                      alt={r.name}
                      className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-3">
                  <div className="font-display text-base text-navy leading-tight">{r.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-saffron mt-1">{r.role}</div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/reformers" data-testid="home-reformers-link" className="inline-flex items-center gap-2 text-navy border-b border-navy/30 hover:border-navy pb-1">
              Read their stories <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* HONOURING THE WOMEN */}
      <section data-testid="home-women" className="py-24 bg-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, #CFA052 0%, transparent 50%), radial-gradient(circle at 80% 70%, #E07A5F 0%, transparent 50%)",
          }}
        />
        <div className="relative section-container">
          <div className="max-w-2xl mx-auto text-center">
            <p className="eyebrow text-saffron">Often unsung, never forgotten</p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl text-navy leading-tight">
              Honouring the women who carried the <span className="italic text-gold">cradle</span>.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed text-[15px]">
              Behind every reformer stood a woman who taught, raised, sheltered or simply
              endured &mdash; turning private courage into the foundation of public revolution.
              These two carry that lineage in our movement.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              {
                id: "savitribai",
                name: "Savitribai Phule",
                title: "India's first woman teacher",
                dates: "1831 — 1897",
                line: "She walked to school each morning with two saris &mdash; one to wear, one to change into when stones, dung and abuse were thrown along the way. She taught with a smile.",
                img: "/images/d1pw3e5i-whatsapp-image-2026-05-06-at-12-34-14.jpeg",
                position: "50% 30%",
              },
              {
                id: "ramabai",
                name: "Mata Ramabai Ambedkar",
                title: "The quiet half of a revolution",
                dates: "1898 — 1935",
                line: "Affectionately known as Ramai, she ran their household on the slenderest of means, raised their children, and stood unshaken through the loss of four young children &mdash; the silent foundation from which a movement was born.",
                img: "/images/wubor5bc-whatsapp-image-2026-05-06-at-05-19-09.jpeg",
                position: "50% 30%",
              },
            ].map((w, i) => (
              <motion.div
                key={w.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`home-women-${w.id}`}
              >
                <Link
                  to={`/reformers#${w.id}`}
                  className="group block bg-pearl border border-navy/10 rounded-sm overflow-hidden hover:-translate-y-1 hover:shadow-2xl transition-all duration-500"
                >
                  <div className="aspect-[5/4] overflow-hidden bg-navy/5">
                    <img
                      src={w.img}
                      alt={w.name}
                      style={{ objectPosition: w.position }}
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-7 sm:p-8 border-t border-gold/30">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-saffron font-semibold">
                      {w.dates}
                    </p>
                    <h3 className="mt-2 font-display text-3xl text-navy leading-tight">
                      {w.name}
                    </h3>
                    <p className="mt-1 text-sm text-gold italic font-display">{w.title}</p>
                    <p
                      className="mt-5 text-slate-600 leading-relaxed text-[14px]"
                      dangerouslySetInnerHTML={{ __html: w.line }}
                    />
                    <div className="mt-6 inline-flex items-center gap-2 text-navy text-sm border-b border-navy/20 group-hover:border-navy pb-0.5">
                      Read her story <ArrowRight size={14} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <p
            className="mt-14 text-center font-display italic text-xl text-gold max-w-2xl mx-auto"
            data-testid="home-women-dedication"
          >
            "A home filled with sacrifice and learning is the cradle of a nation's awakening."
          </p>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="home-cta" className="py-24 bg-navy text-pearl">
        <div className="section-container grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="eyebrow text-gold">Take the first step</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl">Join a community building a better tomorrow.</h2>
          </div>
          <div className="flex flex-wrap gap-4 md:justify-end">
            <Link to="/membership" data-testid="cta-membership" className="px-7 py-3.5 bg-gold text-navy rounded-sm hover:bg-gold-light">
              Become a member
            </Link>
            <Link to="/donate" data-testid="cta-donate" className="px-7 py-3.5 border border-pearl/40 text-pearl rounded-sm hover:bg-pearl/10">
              Make a donation
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
